import * as _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import Helmet from 'react-helmet';
import type { VoidFunctionComponent } from 'react';

import { FULL_QUALITY, PACKAGE_INVENTORY } from '../../../../shared/featureFlags/flagNames';
import { RISK_ISSUE_TYPE } from '../../../../shared/issuesV2';
import { ENTERPRISE_PLATFORM } from '../../../../shared/subscriptions/consts';
import { isFeatureEnabled, useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { useIsQualityEnabled } from '../../../helpers/features/quality';
import { useFetchPolicy } from '../../../hooks/api/policies/useFetchPolicy';
import { useSaveQualityPolicyRules } from '../../../hooks/api/policies/useSaveQualityPolicyRules';
import useEditable from '../../../hooks/useEditable';
import useToggle from '../../../hooks/useToggle';
import FallbackImage from '../../generic/FallbackImage';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import Link from '../../generic/Link/Link';
import Loader from '../../generic/Loader';
import NotFound from '../../generic/NotFound';
import ModalAddDependencyFilterRule from '../../modals/ModalAddDependencyFilterRule';
import SettingsPage from '../../shared/settings/SettingsPage';
import Header from '../../shared/settings/SettingsPageHeader';
import Section from '../../shared/settings/SettingsPageSection';
import EditPolicyCTA from '../EditPolicyCTA';
import type { DependencyFilterVersionType } from '../../../../shared/model-typings';
import type { Policy, RouterProps } from '../../../types';

import ComponentFilterTable from './ComponentFilterTable';
import OutdatedRulesComponent from './OutdatedRulesComponent';
import RiskRuleComponent from './RiskRuleComponent';
import type { OutdatedRule, RuleArray, RuleModalType } from './types';

type DisplayQualityPolicyPageProps = RouterProps<{
  policyId: string;
}>;

type MainContentsProps = {
  policy: Policy;
  policyId: number;
};

const DEFAULT_OUTDATED_RULE: OutdatedRule = {
  type: 'outdated_dependency',
  enabled: false,
  dependencyOutdatedVersionPart: 'MAJOR',
  dependencyOutdatedVersionDiff: 2,
  dependencyOutdatedType: 'SEMVER',
};

const DEFAULT_OUTDATED_FALLBACK_RULE: OutdatedRule = {
  type: 'outdated_dependency',
  enabled: false,
  dependencyOutdatedVersionDiff: 10,
  dependencyOutdatedType: 'FALLBACK',
};

const DisplayQualityPolicyPageMainContents: VoidFunctionComponent<MainContentsProps> = ({
  policy,
  policyId,
}) => {
  const isQualityRestricted = !useIsFeatureEnabled(FULL_QUALITY);

  // Derive server state from policy rules
  const serverRules = useMemo(() => {
    const rules = policy.rules ?? [];
    const groups = _.groupBy(rules, 'type');

    const outdatedRule: OutdatedRule = (
      _.find(groups.outdated_dependency, ['dependencyOutdatedType', 'SEMVER']) as OutdatedRule | undefined
    ) ?? DEFAULT_OUTDATED_RULE;

    const outdatedFallbackRule: OutdatedRule = (
      _.find(groups.outdated_dependency, ['dependencyOutdatedType', 'FALLBACK']) as OutdatedRule | undefined
    ) ?? DEFAULT_OUTDATED_FALLBACK_RULE;

    const allowRules: RuleArray = groups.whitelisted_dependency
      ? [...groups.whitelisted_dependency] as RuleArray
      : [];

    const allDenyRules: RuleArray = groups.blacklisted_dependency
      ? [...groups.blacklisted_dependency] as RuleArray
      : [];

    const isPackageInventoryEnabled = isFeatureEnabled(PACKAGE_INVENTORY);
    const hiddenPackageInventoryRules: RuleArray = isPackageInventoryEnabled
      ? []
      : allDenyRules.filter((rule) => rule.packageProjectLocator);
    const denyRules: RuleArray = isPackageInventoryEnabled
      ? allDenyRules
      : allDenyRules.filter((rule) => !rule.packageProjectLocator);

    const isAbandonwareEnabled = !!groups[RISK_ISSUE_TYPE.RiskAbandonware];
    const isEmptyPackageEnabled = !!groups[RISK_ISSUE_TYPE.RiskEmptyPackage];
    const isNativeCodeEnabled = !!groups[RISK_ISSUE_TYPE.RiskNativeCode];

    return {
      outdatedRule,
      outdatedFallbackRule,
      allowRules,
      denyRules,
      hiddenPackageInventoryRules,
      isAbandonwareEnabled,
      isEmptyPackageEnabled,
      isNativeCodeEnabled,
    };
  }, [policy.rules]);

  // Editable state: tracks dirty local edits against server data
  const { data, isDirty, onUpdate, onSave } = useEditable(serverRules);
  const {
    outdatedRule, outdatedFallbackRule,
    allowRules, denyRules, hiddenPackageInventoryRules,
    isAbandonwareEnabled, isEmptyPackageEnabled, isNativeCodeEnabled,
  } = data;

  // Modal state
  const [isShowingAddRuleModal, onToggleShowAddRuleModal] = useToggle();
  const [addRuleModalType, setRuleModalType] = useState<RuleModalType>('whitelisted_dependency');

  const { mutate: saveRules, isPending } = useSaveQualityPolicyRules(policyId);

  // Save handler
  const onSavePolicy = useCallback(() => {
    const aggregatedRules: RuleArray = [
      ...allowRules,
      ...denyRules,
      ...hiddenPackageInventoryRules,
      { ...outdatedRule },
      { ...outdatedFallbackRule },
    ];

    if (isAbandonwareEnabled) {
      aggregatedRules.push({
        type: RISK_ISSUE_TYPE.RiskAbandonware,
        abandonedMonthsCutoff: 24,
      });
    }
    if (isEmptyPackageEnabled) {
      aggregatedRules.push({
        type: RISK_ISSUE_TYPE.RiskEmptyPackage,
      });
    }
    if (isNativeCodeEnabled) {
      aggregatedRules.push({
        type: RISK_ISSUE_TYPE.RiskNativeCode,
        deniedExtensions: [],
      });
    }

    saveRules({ policyId, rules: aggregatedRules }, { onSuccess: onSave });
  }, [
    allowRules, denyRules, hiddenPackageInventoryRules, outdatedRule, outdatedFallbackRule,
    isAbandonwareEnabled, isEmptyPackageEnabled, isNativeCodeEnabled,
    policyId, saveRules, onSave,
  ]);

  // Outdated rule change handlers
  const onOutdatedRuleChange = useCallback((rule: OutdatedRule) => {
    onUpdate({ outdatedRule: rule });
  }, [onUpdate]);

  const onOutdatedFallbackRuleChange = useCallback((rule: OutdatedRule) => {
    onUpdate({ outdatedFallbackRule: rule });
  }, [onUpdate]);

  // Allow/deny rule handlers
  const onShowAddAllowRule = useCallback(() => {
    setRuleModalType('whitelisted_dependency');
    onToggleShowAddRuleModal();
  }, [onToggleShowAddRuleModal]);

  const onShowAddDenyRule = useCallback(() => {
    setRuleModalType('blacklisted_dependency');
    onToggleShowAddRuleModal();
  }, [onToggleShowAddRuleModal]);

  const onAddFilterRule = useCallback(
    ({ name, startVersion, endVersion, versionType }: {
      name: string;
      startVersion: string;
      endVersion?: string;
      versionType: DependencyFilterVersionType;
    }) => {
      const currentList = addRuleModalType === 'blacklisted_dependency' ? denyRules : allowRules;
      const newList: RuleArray = [...currentList, {
        type: addRuleModalType,
        dependencyFilterName: name,
        dependencyFilterStartVersion: startVersion,
        dependencyFilterEndVersion: endVersion,
        dependencyFilterVersionType: versionType,
        enabled: true,
      }];
      onUpdate(addRuleModalType === 'blacklisted_dependency' ? { denyRules: newList } : { allowRules: newList });
    },
    [addRuleModalType, denyRules, allowRules, onUpdate],
  );

  const onRemoveAllowRule = useCallback((index: number) => {
    const newList = [...allowRules];
    newList.splice(index, 1);
    onUpdate({ allowRules: newList });
  }, [allowRules, onUpdate]);

  const onRemoveDenyRule = useCallback((index: number) => {
    const newList = [...denyRules];
    newList.splice(index, 1);
    onUpdate({ denyRules: newList });
  }, [denyRules, onUpdate]);

  // Risk rule toggle handlers
  const onToggleAbandonware = useCallback((isChecked: boolean) => {
    onUpdate({ isAbandonwareEnabled: isChecked });
  }, [onUpdate]);

  const onToggleEmptyPackage = useCallback((isChecked: boolean) => {
    onUpdate({ isEmptyPackageEnabled: isChecked });
  }, [onUpdate]);

  const onToggleNativeCode = useCallback((isChecked: boolean) => {
    onUpdate({ isNativeCodeEnabled: isChecked });
  }, [onUpdate]);

  return (
    <>
      <EditPolicyCTA policy={policy} />
      <Helmet title={`${policy.title} - Policies`} />
      <div className="container margin-top-lg">
        <SettingsPage>
          <Header heading="Quality Policy">
            <Header.Paragraph>
              Define rules based on the version and/or name of your dependencies
            </Header.Paragraph>
            <Header.SaveButton onClick={onSavePolicy} isDisabled={!isDirty} isLoading={isPending} />
          </Header>

          <PaidFeatureGate isRestricted={isQualityRestricted} planToShow={ENTERPRISE_PLATFORM}>
            <Section>
              <Section.Left heading="Stale Package Prevention">
                <Section.Paragraph>
                  These rules will flag any dependencies that are out of date. If the versions
                  involved are not valid{' '}
                  <Link to="https://semver.org/" openInNewTab>
                    semantic versions
                  </Link>
                  , fallback rules can be set.
                </Section.Paragraph>
              </Section.Left>
              <Section.Right>
                <OutdatedRulesComponent
                  outdatedRule={outdatedRule}
                  onOutdatedRuleChange={onOutdatedRuleChange}
                  outdatedFallbackRule={outdatedFallbackRule}
                  onOutdatedFallbackRuleChange={onOutdatedFallbackRuleChange}
                />
              </Section.Right>
            </Section>

            <Section>
              <Section.Left heading="Allow Rules">
                <Section.Paragraph>
                  Selected dependencies will never raise an issue -- even when in violation of another policy.
                </Section.Paragraph>
              </Section.Left>
              <Section.Right isBorderless>
                <ComponentFilterTable
                  onAddRule={onShowAddAllowRule}
                  rules={allowRules}
                  removeRule={onRemoveAllowRule}
                />
              </Section.Right>
            </Section>

            <Section>
              <Section.Left heading="Block Rules">
                <Section.Paragraph>
                  Selected dependencies will always raise an issue.
                </Section.Paragraph>
              </Section.Left>
              <Section.Right isBorderless>
                <ComponentFilterTable
                  onAddRule={onShowAddDenyRule}
                  rules={denyRules}
                  removeRule={onRemoveDenyRule}
                />
              </Section.Right>
            </Section>

            <Section>
              <Section.Left heading="Risk Intelligence">
                <Section.Paragraph>
                  Configure risk detection rules for your dependencies.
                </Section.Paragraph>
              </Section.Left>
              <Section.Right>
                <RiskRuleComponent
                  title="Abandonware"
                  description="Dependencies that haven't received updates from their maintainers in +2 years are considered abandoned."
                  supportedFetchers={['npm', 'mvn', 'pip']}
                  isEnabled={isAbandonwareEnabled}
                  onToggle={onToggleAbandonware}
                />
                <RiskRuleComponent
                  title="Empty Package"
                  description="Package has no code and should not be used."
                  supportedFetchers={['npm', 'mvn', 'pip']}
                  isEnabled={isEmptyPackageEnabled}
                  onToggle={onToggleEmptyPackage}
                />
                <RiskRuleComponent
                  title="Native Code Detection"
                  description="Detects compiled executables that are not expected within an ecosystem/language."
                  supportedFetchers={['npm', 'pip']}
                  isEnabled={isNativeCodeEnabled}
                  onToggle={onToggleNativeCode}
                />
              </Section.Right>
            </Section>
          </PaidFeatureGate>
        </SettingsPage>
      </div>

      <ModalAddDependencyFilterRule
        show={isShowingAddRuleModal}
        onHide={onToggleShowAddRuleModal}
        onAddRule={onAddFilterRule}
        ruleType={addRuleModalType}
      />
    </>
  );
};

const DisplayQualityPolicyPage: VoidFunctionComponent<DisplayQualityPolicyPageProps> = (props) => {
  const policyId = Number(props.params.policyId);
  const hasQuality = useIsQualityEnabled();
  const isValidPage = hasQuality && !Number.isNaN(policyId);
  const { data: policy, isLoading, isError } = useFetchPolicy(policyId, isValidPage);

  if (!isValidPage) {
    return <NotFound />;
  }
  if (isLoading) {
    return <Loader />;
  }
  if (isError) {
    return (
      <FallbackImage src="error-plug" alt="Error loading policy" hasPadding>
        Something went wrong loading this policy. Please try again later.
      </FallbackImage>
    );
  }
  if (!policy) {
    return <NotFound />;
  }

  return <DisplayQualityPolicyPageMainContents policy={policy} policyId={policyId} />;
};

export default DisplayQualityPolicyPage;
